/**
 * MessengerChatHeads
 *
 * Facebook Messenger–style floating chat windows mounted globally in
 * DashboardLayout so the user can keep conversations open while navigating
 * any page.
 *
 * Behaviour (per user request):
 *  - Unlimited concurrent windows (stacked from bottom-right leftwards)
 *  - Popups stay open until the user manually ✕ closes them
 *  - Draggable window headers
 *  - Minimize to a chat head bubble; click again to restore
 *  - Pin toggle persists the window across page reloads (localStorage)
 *  - Sound on/off preference saved to user account (`/me/preferences`) so it
 *    is synced across devices.
 *
 * The component subscribes to the `ws:chat_message` custom event dispatched
 * by DashboardLayout, so we don't duplicate the WebSocket hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import {
  X, Minus, Maximize2, Send, Pin, PinOff, Volume2, VolumeX, MessageSquare, Paperclip, Check, CheckCheck,
} from 'lucide-react';

const OPEN_WINDOWS_KEY = 'messenger-open-windows';
const WINDOW_POS_KEY = 'messenger-window-positions';

// Lightweight "ding" via WebAudio so we don't need to ship an audio file.
const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
    osc.onended = () => ctx.close();
  } catch {
    // ignore; some browsers require a user gesture before AudioContext
  }
};

const readJSON = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const writeJSON = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

export default function MessengerChatHeads() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Per-window state: { user_id, user_name, messages:[], minimized:bool, pinned:bool, loaded:bool, draft:'' }
  const [windows, setWindows] = useState(() => {
    const pinned = readJSON(OPEN_WINDOWS_KEY, []);
    return pinned.map((w) => ({ ...w, messages: [], loaded: false, draft: '', minimized: false }));
  });
  const windowsRef = useRef(windows);
  useEffect(() => { windowsRef.current = windows; }, [windows]);

  // Map of { user_id: timeoutHandle } to auto-clear "typing" after 3 s of
  // silence. typingUsers is a Set of user_ids currently typing to us.
  const [typingUsers, setTypingUsers] = useState({}); // { user_id: timestamp }
  const typingClearRef = useRef({}); // { user_id: setTimeout handle }
  const typingThrottleRef = useRef({}); // { user_id: lastSentAt }

  // Persist pinned windows so they survive reloads. Unpinned ones vanish.
  useEffect(() => {
    const toPersist = windows.filter((w) => w.pinned).map(({ user_id, user_name, pinned }) => ({ user_id, user_name, pinned }));
    writeJSON(OPEN_WINDOWS_KEY, toPersist);
  }, [windows]);

  // User preferences (sound on/off) — synced with backend.
  const [soundOn, setSoundOn] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/me/preferences');
        if (!cancelled && res.data?.success) {
          setSoundOn(!!res.data.data?.chat_sound_enabled);
        }
      } catch {
        /* ignore — defaults to on */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleSound = useCallback(async () => {
    const next = !soundOn;
    setSoundOn(next);
    try {
      await api.put('/me/preferences', { chat_sound_enabled: next });
      toast.success(next ? 'Sohbet sesi açıldı' : 'Sohbet sesi kapatıldı');
    } catch {
      toast.error('Tercih kaydedilemedi');
      setSoundOn(!next);
    }
  }, [soundOn]);

  // Open (or focus) a window for a given contact and optionally append a
  // freshly-received message. Also fetches message history on first open.
  const openWindow = useCallback(async (contact, incomingMsg) => {
    setWindows((prev) => {
      const idx = prev.findIndex((w) => w.user_id === contact.user_id);
      if (idx >= 0) {
        const next = [...prev];
        const existing = { ...next[idx], minimized: false };
        if (incomingMsg && !existing.messages.some((m) => m.id === incomingMsg.id)) {
          existing.messages = [...existing.messages, incomingMsg];
        }
        next[idx] = existing;
        return next;
      }
      return [
        ...prev,
        {
          user_id: contact.user_id,
          user_name: contact.user_name,
          messages: incomingMsg ? [incomingMsg] : [],
          minimized: false,
          pinned: contact.pinned || false,
          loaded: false,
          draft: '',
        },
      ];
    });

    // Load history lazily once per window
    const already = windowsRef.current.find((w) => w.user_id === contact.user_id);
    if (!already?.loaded) {
      try {
        const res = await api.get(`/chat/history/${contact.user_id}?per_page=30`);
        if (res.data?.success) {
          const history = res.data.data || [];
          setWindows((prev) => prev.map((w) =>
            w.user_id === contact.user_id
              ? {
                  ...w,
                  loaded: true,
                  // Merge: keep any incoming msgs that slipped in
                  messages: [...history, ...w.messages.filter((m) => !history.some((h) => h.id === m.id))],
                }
              : w
          ));
          // /chat/history endpoint auto-marks partner messages as read
        }
      } catch {
        /* keep window open even if history failed */
      }
    }
  }, []);

  const closeWindow = useCallback((user_id) => {
    setWindows((prev) => prev.filter((w) => w.user_id !== user_id));
  }, []);

  const toggleMinimize = useCallback((user_id) => {
    setWindows((prev) => prev.map((w) => (w.user_id === user_id ? { ...w, minimized: !w.minimized } : w)));
  }, []);

  const togglePin = useCallback((user_id) => {
    setWindows((prev) => prev.map((w) => (w.user_id === user_id ? { ...w, pinned: !w.pinned } : w)));
  }, []);

  const updateDraft = useCallback((user_id, draft) => {
    setWindows((prev) => prev.map((w) => (w.user_id === user_id ? { ...w, draft } : w)));
    // Throttled typing signal (2s) — piggybacks on the global WS helper
    // exposed by DashboardLayout via window.__sendWsMessage.
    try {
      const sendFn = window.__sendWsMessage;
      const now = Date.now();
      if (sendFn && (!typingThrottleRef.current[user_id] || now - typingThrottleRef.current[user_id] > 2000)) {
        typingThrottleRef.current[user_id] = now;
        sendFn({ type: 'chat_typing', target_id: user_id });
      }
    } catch {}
  }, []);

  const sendMessage = useCallback(async (user_id) => {
    const win = windowsRef.current.find((w) => w.user_id === user_id);
    if (!win || !win.draft.trim()) return;
    const text = win.draft.trim();
    updateDraft(user_id, '');
    try {
      const fd = new FormData();
      fd.append('receiver_id', user_id);
      fd.append('message', text);
      await api.post('/chat/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Incoming message will also come back via WS (server emits to sender too)
      // so we don't optimistically append — avoids duplicates.
    } catch {
      toast.error('Mesaj gönderilemedi');
      updateDraft(user_id, text);
    }
  }, [updateDraft]);

  const sendFile = useCallback(async (user_id, file) => {
    if (!file) return;
    // 20MB limit (matches backend MAX_CHAT_ATTACHMENT_SIZE)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Dosya 20MB sınırını aşıyor');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('receiver_id', user_id);
      fd.append('message', '');
      fd.append('file', file);
      await api.post('/chat/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dosya gönderildi');
    } catch {
      toast.error('Dosya gönderilemedi');
    }
  }, []);

  // Listen for incoming chat messages (both directions: sender also receives
  // an echo via WS). Opens / reveals the relevant window and plays sound if
  // the message is FROM someone else and sound is enabled.
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg) return;
      // Identify the "other party" relative to current user
      const otherId = msg.sender_id === user?.id ? msg.receiver_id : msg.sender_id;
      const otherName = msg.sender_id === user?.id ? msg.receiver_name : msg.sender_name;
      const isIncoming = msg.sender_id !== user?.id;

      // Open window on INCOMING messages (don't auto-open just because we sent one)
      if (isIncoming) {
        openWindow({ user_id: otherId, user_name: otherName }, msg);
        if (soundOn) playDing();
      } else {
        // Outgoing — if the recipient window is open, append there
        setWindows((prev) => prev.map((w) => (
          w.user_id === otherId && !w.messages.some((m) => m.id === msg.id)
            ? { ...w, messages: [...w.messages, msg] }
            : w
        )));
      }
    };
    window.addEventListener('ws:chat_message', handler);
    return () => window.removeEventListener('ws:chat_message', handler);
  }, [user?.id, soundOn, openWindow]);

  // Global helper: any code can dispatch `open-chat-head` to open a conversation.
  useEffect(() => {
    const handler = (e) => {
      const { user_id, user_name } = e.detail || {};
      if (user_id && user_name) openWindow({ user_id, user_name });
    };
    window.addEventListener('open-chat-head', handler);
    return () => window.removeEventListener('open-chat-head', handler);
  }, [openWindow]);

  // Typing indicator — incoming. DashboardLayout dispatches `ws:chat_typing`
  // for every chat_typing WS frame. We only care when the current user is the
  // target, i.e. the partner is the one typing.
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail || {};
      // Backend relays {type:'chat_typing', user_id, user_name} → "user_id" is
      // the sender (i.e. the partner typing TO us). target_id is only on the
      // outgoing side from Chat.jsx, not on the incoming side.
      const partner = data.user_id || data.sender_id;
      if (!partner || partner === user?.id) return;
      setTypingUsers((prev) => ({ ...prev, [partner]: Date.now() }));
      clearTimeout(typingClearRef.current[partner]);
      typingClearRef.current[partner] = setTimeout(() => {
        setTypingUsers((prev) => {
          const n = { ...prev };
          delete n[partner];
          return n;
        });
      }, 3000);
    };
    window.addEventListener('ws:chat_typing', handler);
    return () => window.removeEventListener('ws:chat_typing', handler);
  }, [user?.id]);

  // Online users — DashboardLayout exposes the full list via
  // `ws:online_status` and `window.__onlineUsers`. Normalise to a Set for O(1)
  // `isOnline` checks inside each window.
  const [onlineSet, setOnlineSet] = useState(() => new Set(window.__onlineUsers || []));
  useEffect(() => {
    const handler = (e) => setOnlineSet(new Set(e.detail || []));
    window.addEventListener('ws:online_status', handler);
    return () => window.removeEventListener('ws:online_status', handler);
  }, []);

  // Read receipts — when the partner opens our conversation, the server
  // fires `chat_read` with `reader_id = partner`. Flip all our outgoing
  // (mine=true, receiver_id=reader_id) messages to read.
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail || {};
      const readerId = data.reader_id;
      if (!readerId) return;
      setWindows((prev) => prev.map((w) =>
        w.user_id === readerId
          ? {
              ...w,
              messages: w.messages.map((m) =>
                m.sender_id === user?.id && m.receiver_id === readerId && !m.read
                  ? { ...m, read: true, read_at: new Date().toISOString() }
                  : m
              ),
            }
          : w
      ));
    };
    window.addEventListener('ws:chat_read', handler);
    return () => window.removeEventListener('ws:chat_read', handler);
  }, [user?.id]);

  if (!user) return null;

  return (
    <>
      {/* Render a row of chat windows. Sound-toggle button has been moved
          inside each window's header (see ChatHead) so it doesn't leak under
          the collapsed sidebar — and it only shows where it's actually
          needed: when the user is actively chatting. The full Chat page
          (/dashboard/chat) exposes the same toggle in its toolbar via the
          `chatSoundEnabled` window property. */}
      <div className="fixed bottom-0 right-4 z-40 flex items-end gap-3 pointer-events-none">
        {windows.map((w, i) => (
          <ChatHead
            key={w.user_id}
            win={w}
            indexFromRight={i}
            selfId={user.id}
            isTyping={!!typingUsers[w.user_id]}
            isOnline={onlineSet.has(w.user_id)}
            soundOn={soundOn}
            onToggleSound={toggleSound}
            onClose={() => closeWindow(w.user_id)}
            onMinimize={() => toggleMinimize(w.user_id)}
            onPin={() => togglePin(w.user_id)}
            onOpenFull={() => navigate(`/dashboard/chat?user=${w.user_id}`)}
            onChangeDraft={(v) => updateDraft(w.user_id, v)}
            onSend={() => sendMessage(w.user_id)}
            onSendFile={(file) => sendFile(w.user_id, file)}
          />
        ))}
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Single chat window — draggable, minimizable, pinnable.
// --------------------------------------------------------------------------
function ChatHead({ win, indexFromRight, selfId, isTyping, isOnline, soundOn, onToggleSound, onClose, onMinimize, onPin, onOpenFull, onChangeDraft, onSend, onSendFile }) {
  const positions = readJSON(WINDOW_POS_KEY, {});
  const savedPos = positions[win.user_id];
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropOverlayRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);
  const [pos, setPos] = useState(savedPos || null); // {x, y} custom position
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [win.messages.length, win.minimized]);

  // Persist window position whenever it changes
  useEffect(() => {
    if (!pos) return;
    const all = readJSON(WINDOW_POS_KEY, {});
    all[win.user_id] = pos;
    writeJSON(WINDOW_POS_KEY, all);
  }, [pos, win.user_id]);

  const startDrag = (e) => {
    dragRef.current.dragging = true;
    dragRef.current.moved = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.origX = pos?.x ?? 0;
    dragRef.current.origY = pos?.y ?? 0;
    const onMove = (ev) => {
      if (!dragRef.current.dragging) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
      setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Default stacking position: anchored bottom-right, offset by index
  const defaultRight = indexFromRight * 340;
  const style = pos
    ? { position: 'fixed', left: pos.x, top: pos.y, bottom: 'auto', right: 'auto' }
    : { position: 'fixed', right: defaultRight + 16, bottom: 60 };

  if (win.minimized) {
    return (
      <button
        type="button"
        onClick={onMinimize}
        className="pointer-events-auto h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg flex items-center justify-center border-2 border-white dark:border-slate-900 hover:scale-105 transition-transform mb-4"
        title={`${win.user_name} ile sohbet`}
        data-testid={`chat-head-min-${win.user_id}`}
        style={style}
      >
        <span className="text-sm font-bold">{win.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
      </button>
    );
  }

  return (
    <div
      className={`pointer-events-auto w-[320px] h-[420px] bg-white dark:bg-slate-800 rounded-t-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col mb-0 relative ${dragOver ? 'ring-2 ring-emerald-400' : ''}`}
      style={style}
      data-testid={`chat-head-${win.user_id}`}
      onDragOver={(e) => { e.preventDefault(); if (!dropOverlayRef.current) { dropOverlayRef.current = true; setDragOver(true); } }}
      onDragLeave={(e) => {
        // Only clear if we actually leave the window bounds
        if (e.currentTarget === e.target) { dropOverlayRef.current = false; setDragOver(false); }
      }}
      onDrop={(e) => {
        e.preventDefault();
        dropOverlayRef.current = false;
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) onSendFile?.(file);
      }}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-emerald-500/10 backdrop-blur-sm pointer-events-none">
          <div className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold">
            Dosyayı bırakın
          </div>
        </div>
      )}
      {/* Header (drag handle) */}
      <div
        onMouseDown={startDrag}
        className="bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-2 flex items-center justify-between cursor-move select-none"
      >
        <div className="flex items-center gap-2 text-white min-w-0">
          <div className="relative shrink-0">
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-xs font-bold">{win.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
            </div>
            {/* Online presence dot */}
            <span
              className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-emerald-500 ${isOnline ? 'bg-emerald-300' : 'bg-slate-300'}`}
              title={isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
              data-testid={`chat-head-online-${win.user_id}`}
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold truncate leading-tight">{win.user_name}</span>
            <span className="text-[10px] opacity-80 truncate">{isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 text-white/80">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSound?.(); }}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title={soundOn ? 'Sohbet sesini kapat' : 'Sohbet sesini aç'}
            data-testid={`chat-head-sound-${win.user_id}`}
          >
            {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
          </button>
          <button onClick={onPin} className="p-1 hover:bg-white/20 rounded transition-colors" title={win.pinned ? 'Sabitlemeyi kaldır' : 'Sayfalar arası sabitle'} data-testid={`chat-head-pin-${win.user_id}`}>
            {win.pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
          </button>
          <button onClick={onOpenFull} className="p-1 hover:bg-white/20 rounded transition-colors" title="Tam ekran aç">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button onClick={onMinimize} className="p-1 hover:bg-white/20 rounded transition-colors" title="Küçült" data-testid={`chat-head-minimize-${win.user_id}`}>
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded transition-colors" title="Kapat" data-testid={`chat-head-close-${win.user_id}`}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Message list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 bg-slate-50 dark:bg-slate-900/40 text-sm"
      >
        {win.messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
            <span className="text-xs">Henüz mesaj yok</span>
          </div>
        )}
        {win.messages.map((m) => {
          const mine = m.sender_id === selfId;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[75%] flex flex-col items-end gap-0.5">
                <div
                  className={`px-3 py-1.5 rounded-2xl text-xs break-words ${
                    mine
                      ? 'bg-emerald-500 text-white rounded-br-sm'
                      : 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-bl-sm border border-slate-200 dark:border-slate-600'
                  }`}
                >
                  {m.message || (m.attachment?.file_name ? `📎 ${m.attachment.file_name}` : '')}
                </div>
                {mine && (
                  <span
                    className={`text-[10px] flex items-center gap-0.5 ${m.read ? 'text-emerald-500' : 'text-slate-400'}`}
                    data-testid={`chat-head-receipt-${m.id}`}
                    title={m.read ? 'Okundu' : 'Gönderildi'}
                  >
                    {m.read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {isTyping && (
          <div className="flex justify-start" data-testid={`chat-head-typing-${win.user_id}`}>
            <div className="bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-slate-200 dark:border-slate-600 text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '300ms' }} />
              <span className="ml-1">yazıyor</span>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); onSend(); }}
        className="border-t border-slate-200 dark:border-slate-700 px-2 py-2 flex items-center gap-1.5 bg-white dark:bg-slate-800"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSendFile?.(f);
            e.target.value = '';
          }}
          data-testid={`chat-head-file-input-${win.user_id}`}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="h-7 w-7 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center shrink-0"
          title="Dosya/resim gönder"
          data-testid={`chat-head-attach-${win.user_id}`}
        >
          <Paperclip className="h-3.5 w-3.5" />
        </button>
        <input
          type="text"
          value={win.draft}
          onChange={(e) => onChangeDraft(e.target.value)}
          placeholder="Mesaj yazın..."
          className="flex-1 text-xs px-2 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 outline-none focus:border-emerald-400"
          data-testid={`chat-head-input-${win.user_id}`}
        />
        <button
          type="submit"
          disabled={!win.draft.trim()}
          className="h-7 w-7 rounded-full bg-emerald-500 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white flex items-center justify-center hover:bg-emerald-600 transition-colors shrink-0"
          data-testid={`chat-head-send-${win.user_id}`}
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </form>
    </div>
  );
}
